import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Xmark, LockSquare, Check, Eye, EyeClosed } from "iconoir-react";
import { setOwnPassword } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

export default function SetPasswordDialog({ open, onClose, hasPassword }) {
    const { t } = useT();
    const [pw, setPw] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 6) {
            toast.error(t("setpw.too_short"));
            return;
        }
        setBusy(true);
        try {
            await setOwnPassword(pw);
            toast.success(hasPassword ? t("setpw.updated") : t("setpw.saved"));
            setPw("");
            onClose?.();
        } catch {
            toast.error(t("setpw.save_failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm p-4"
                    onClick={onClose}
                    data-testid="set-password-dialog"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white rounded-[28px] shadow-[0_2px_4px_rgba(13,42,27,0.04),0_24px_48px_rgba(13,42,27,0.12)] p-6 sm:p-7"
                    >
                        <div className="flex items-start justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#e8f1e4] flex items-center justify-center">
                                    <LockSquare className="w-5 h-5 text-[#2F6B43]" />
                                </div>
                                <div>
                                    <div className="font-display text-2xl font-bold tracking-tight leading-none">
                                        {hasPassword ? t("setpw.change_headline") : t("setpw.set_headline")}
                                    </div>
                                    <div className="text-xs text-[#5b6d63] font-semibold mt-1">
                                        {hasPassword ? t("setpw.change_sub") : t("setpw.set_sub")}
                                    </div>
                                </div>
                            </div>
                            <button
                                data-testid="setpw-close-btn"
                                onClick={onClose}
                                className="w-8 h-8 rounded-full hover:bg-[#faf5ea] flex items-center justify-center"
                                aria-label="close"
                            >
                                <Xmark className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <div className="mb-2 text-sm font-semibold text-[#0d2a1b]">{t("setpw.new_password")}</div>
                                <div className="relative">
                                    <input
                                        data-testid="account-password-input"
                                        type={showPw ? "text" : "password"}
                                        minLength={6}
                                        className="brut-input pr-11"
                                        placeholder={t("setpw.placeholder")}
                                        value={pw}
                                        onChange={(e) => setPw(e.target.value)}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw((s) => !s)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center opacity-60 hover:opacity-100"
                                    >
                                        {showPw ? <EyeClosed className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={busy || pw.length < 6}
                                data-testid="account-password-save-btn"
                                className="w-full flex items-center justify-center gap-2 bg-[#2F6B43] text-white rounded-2xl py-3 font-semibold text-sm hover:bg-[#255636] transition-colors shadow-[0_4px_12px_rgba(47,107,67,0.25)] disabled:opacity-50"
                            >
                                <Check className="w-4 h-4" /> {busy ? t("setpw.saving") : hasPassword ? t("setpw.update_btn") : t("setpw.save_btn")}
                            </button>
                            <div className="text-[11px] text-[#5b6d63] text-center">
                                {t("setpw.footer")}
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
